/**
 * localStorage-backed "My Library" bookmarks.
 * We only persist episode IDs — episode data is refetched from /api/episodes.
 * Cross-tab sync via the `storage` event + a custom `library:change` event.
 */
import { useCallback, useEffect, useState } from 'react';

const KEY = 'aird.library.v1';
const EVENT = 'library:change';

const readAll = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const writeAll = (ids) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: ids }));
  } catch {
    /* quota exceeded — silently ignore */
  }
};

export const useLibrary = () => {
  const [ids, setIds] = useState(readAll);

  useEffect(() => {
    const onLocal = (e) => setIds(e.detail || readAll());
    const onStorage = (e) => { if (e.key === KEY) setIds(readAll()); };
    window.addEventListener(EVENT, onLocal);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onLocal);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback((id) => {
    const current = readAll();
    const next = current.includes(id) ? current.filter((x) => x !== id) : [id, ...current];
    writeAll(next);
    return !current.includes(id); // returns whether it's now saved
  }, []);

  const remove = useCallback((id) => {
    const next = readAll().filter((x) => x !== id);
    writeAll(next);
  }, []);

  const clear = useCallback(() => writeAll([]), []);

  return { ids, count: ids.length, has, toggle, remove, clear };
};
